import { DetectionZone, facilityToDevice, LaneLine, ScreenLine, TargetSource, TrackedTarget } from "@app/shared";
import React, { useMemo } from "react";
import { ChartBounds, DataChart, DataPoint, DatasetConfig, TargetDatasetConfig } from "../DataChart";

export type LayoutChartProps = {
    targets: TrackedTarget[];
    laneLines: LaneLine[];
    detectionZones: DetectionZone[];
    screenLines: ScreenLine[];
    bounds?: ChartBounds;
    devicePosition?: { x: number; y: number };
    deviceHeading?: number;
    videoFrame: number;
    radarFrame: number;
    onTargetClick?: (datasetIndex: number, pointIndex: number, point: DataPoint, event?: MouseEvent) => void;
};

export const LayoutChart: React.FC<LayoutChartProps> = (props) => {
    const devicePos = props.devicePosition ?? { x: 0, y: 0 };
    const deviceHeading = props.deviceHeading ?? 0;

    // Create target datasets separated by type
    const targetDatasets = useMemo((): TargetDatasetConfig[] => {
        if (!props.targets || props.targets.length === 0) return [];

        const radarOnlyTargets = props.targets.filter(target =>
            target.source === TargetSource.Radar && (!target?.associatedCameraTarget)
        );
        const cameraOnlyTargets = props.targets.filter(target =>
            target.source === TargetSource.Camera
        );
        const fusedTargets = props.targets.filter(target =>
            target?.associatedCameraTarget
        );

        // Helper function to create target data
        const createTargetData = (targets: TrackedTarget[]) => {
            return targets.map((target, index) => {

                let currentPosition = target.position;

                if (target.targetHistory && target.targetHistory.length > 0) {
                    const frameToFind = target.source === TargetSource.Radar ? props.radarFrame : props.videoFrame;
                    let historyEntry = target.targetHistory.find(history => history.frameNumber === frameToFind);
                    if (!historyEntry && target.associatedCameraTarget) {
                        //Try to get the position from the associated camera target
                        historyEntry = target.associatedCameraTarget.targetHistory.find(history => history.frameNumber === props.videoFrame)
                    }

                    if (historyEntry)
                        currentPosition = historyEntry.position;
                }

                return {
                    x: currentPosition.x,
                    y: currentPosition.y,
                    z: currentPosition.z,
                    key: target,
                    targetId: target.radarId,
                    pointIndex: index,
                    targetType: target?.associatedCameraTarget ? 'fused' :
                        (target.source === TargetSource.Camera ? 'camera' : 'radar')
                };
            });
        };

        const datasets: TargetDatasetConfig[] = [];

        if (radarOnlyTargets.length > 0) {
            datasets.push({
                label: "Radar-Only Targets",
                data: createTargetData(radarOnlyTargets),
                baseColor: "#FF0000",
                selectedColor: "#FF0000",
                baseRadius: 6,
                borderColor: "#FFFFFF",
                borderWidth: 2
            });
        }

        if (cameraOnlyTargets.length > 0) {
            datasets.push({
                label: "Camera-Only Targets",
                data: createTargetData(cameraOnlyTargets),
                baseColor: "#0066FF",
                selectedColor: "#0066FF",
                baseRadius: 6,
                borderColor: "#FFFFFF",
                borderWidth: 2
            });
        }

        if (fusedTargets.length > 0) {
            datasets.push({
                label: "Fused Targets",
                data: createTargetData(fusedTargets),
                baseColor: "#00AA00",
                selectedColor: "#00AA00",
                baseRadius: 8,
                borderColor: "#FFFFFF",
                borderWidth: 2
            });
        }

        return datasets;
    }, [props.targets, props.radarFrame, props.videoFrame]);

    // Create other datasets (detection zones, screen lines)
    const otherDatasets = useMemo((): DatasetConfig[] => {
        const datasets: DatasetConfig[] = [];

        // Add detection zones if available
        if (props.detectionZones && props.detectionZones.length > 0) {
            props.detectionZones.forEach((zone, i) => {
                if (!zone.points || zone.points.length < 3) return;

                const transformedPoints = facilityToDevice(zone.points, devicePos, deviceHeading);
                const closedPoints = [...transformedPoints, transformedPoints[0]];

                const zoneData = closedPoints.map((point, index) => ({
                    x: point.x,
                    y: point.y,
                    z: 0,
                    pointIndex: index
                }));

                datasets.push({
                    label: `Detection Zone ${zone.name || i + 1}`,
                    data: zoneData,
                    color: "#00AA4440", // With transparency
                    pointRadius: 0,
                    showLine: true,
                    fill: true,
                });
            });
        }

        // Add screen lines if available
        if (props.screenLines && props.screenLines.length > 0) {
            props.screenLines.forEach((screenLine, i) => {
                if (!screenLine.points || screenLine.points.length < 2) return;

                const transformedPoints = facilityToDevice(screenLine.points, devicePos, deviceHeading);
                const lineData = transformedPoints.map((point, index) => ({
                    x: point.x,
                    y: point.y,
                    z: 0,
                    pointIndex: index
                }));

                datasets.push({
                    label: `Screen Line ${screenLine.name || i + 1}`,
                    data: lineData,
                    color: "#000044",
                    borderColor: "#000044",
                    pointRadius: 2,
                    borderWidth: 2,
                    showLine: true,
                });
            });
        }

        // Add lane lines if available
        if (props.laneLines && props.laneLines.length > 0) {
            props.laneLines.forEach((laneLine, i) => {
                if (!laneLine.points || laneLine.points.length < 2) return;

                const transformedPoints = facilityToDevice(laneLine.points, devicePos, deviceHeading);
                const lineData = transformedPoints.map((point, index) => ({
                    x: point.x,
                    y: point.y,
                    z: 0,
                    pointIndex: index
                }));

                datasets.push({
                    label: `Lane Line ${laneLine.name || i + 1}`,
                    data: lineData,
                    color: "#AA0044",
                    borderColor: "#AA0044",
                    borderWidth: 2,
                    pointRadius: 2,
                    showLine: true,
                });
            });
        }

        return datasets;
    }, [props.detectionZones, props.screenLines, devicePos, deviceHeading]);

    // Transform lane lines for coordinate system
    const transformedLaneLines = useMemo(() => {
        if (!props.laneLines) return [];

        return props.laneLines.map(laneLine => ({
            ...laneLine,
            points: facilityToDevice(laneLine.points || [], devicePos, deviceHeading)
        }));
    }, [props.laneLines, devicePos, deviceHeading]);

    return (
        <DataChart
            targetDatasets={targetDatasets}
            datasets={otherDatasets}
            bounds={props.bounds}
            showCursorPosition={true}
            height="500px"
            onPointClick={props.onTargetClick}
        />
    );
};
